import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export interface FormFieldProps {
  label: string;
  children?: ReactNode; // For iframe container
  error?: boolean;
  valid?: boolean | null;
  focus?: boolean;
  span?: string; // grid column span e.g. 'span 3'
  htmlFor?: string;
  // Optional plain input element (TextField already renders its own label normally, but we hide and use ours)
  input?: ReactNode;
}

/**
 * Standardized form field wrapper to give consistent label, spacing, border, and focus/valid states.
 * Supports either an iframe container (children) or a provided input element.
 */
export function FormField({ label, children, input, error, valid, focus, span, htmlFor }: FormFieldProps) {
  const stateColor = focus ? 'primary.main' : (valid == null ? '#dfdfdf' : valid ? 'success.main' : 'error.main');
  const borderColor = error ? 'error.main' : stateColor;
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap:0.5, gridColumn: span }}>
      <Typography variant="caption" component="label" htmlFor={htmlFor} sx={{ fontWeight:500 }}>{label}</Typography>
      {children ? (
        <Box sx={{
          border: '1px solid',
          borderColor,
          boxShadow: focus ? 1 : 0,
          borderRadius: 1,
          height: 56,
          display:'flex',
          alignItems:'center',
          px:1,
          backgroundColor:'#fff',
          transition:'border-color 120ms, box-shadow 120ms'
        }}>
          {children}
        </Box>
      ) : (
        <Box sx={{
          '& .MuiInputBase-root': {
            height:56,
            background:'#fff'
          },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColor + '!important' },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main !important' },
          '& .MuiInputBase-input::placeholder': {
            color: '#b5b5b5',
            fontWeight: 300,
            opacity: 1
          }
        }}>
          {input}
        </Box>
      )}
    </Box>
  );
}
